import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'admin-sidebar-favorites';
const MODE_STORAGE_KEY = 'admin-sidebar-mode';

const DEFAULT_FAVORITES = [
  '/home',
  '/courts',
  '/users',
  '/latest-booked-games',
  '/calendar',
  '/transactions',
];

function getStoredPaths(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw == null) {
      // Seed defaults for first-time users (no favorites AND no stored mode)
      const hasMode = window.localStorage.getItem(MODE_STORAGE_KEY);
      if (!hasMode) return DEFAULT_FAVORITES;
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((p): p is string => typeof p === 'string');
  } catch {
    return [];
  }
}

export function useAdminSidebarFavorites() {
  const [favoritePaths, setFavoritePaths] = useState<string[]>(() =>
    getStoredPaths()
  );

  useEffect(() => {
    const handler = () => setFavoritePaths(getStoredPaths());
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const setFavorites = useCallback((paths: string[]) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(paths));
      setFavoritePaths(paths);
    } catch {
      // ignore
    }
  }, []);

  const addFavorite = useCallback((path: string) => {
    const current = getStoredPaths();
    if (current.includes(path)) return;
    setFavorites([...current, path]);
  }, [setFavorites]);

  const removeFavorite = useCallback((path: string) => {
    setFavorites(getStoredPaths().filter((p) => p !== path));
  }, [setFavorites]);

  const toggleFavorite = useCallback((path: string) => {
    const current = getStoredPaths();
    if (current.includes(path)) {
      setFavorites(current.filter((p) => p !== path));
    } else {
      setFavorites([...current, path]);
    }
  }, [setFavorites]);

  const reorderFavorites = useCallback((fromIndex: number, toIndex: number) => {
    const current = getStoredPaths();
    if (fromIndex < 0 || fromIndex >= current.length || toIndex < 0 || toIndex >= current.length) return;
    const next = [...current];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setFavorites(next);
  }, [setFavorites]);

  const isFavorite = useCallback(
    (path: string) => favoritePaths.includes(path),
    [favoritePaths]
  );

  return {
    favoritePaths,
    setFavorites,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    reorderFavorites,
    isFavorite,
  };
}
